/**
 * 6-tab shell for the Dossier Detail page (Phase 1 UX).
 *
 * Phase 1 scope:
 *   - Tab "Vérif documents": renders existing dossier content (passed as children)
 *   - 5 other tabs: placeholder cards announcing "to come in Phase 2/3"
 *   - Each tab shows its own status pill (editable: pending → in_progress → done)
 */
import React, { useState } from "react";
import { OPEN_SLOT_EVENT } from "./MissingDocsCard";
import { LayoutDashboard, LifeBuoy, FileCheck2, Banknote, FileSignature, Hammer, Calculator, FolderOpen, Clock } from "lucide-react";
import { api } from "../lib/api";
import TabContentEditor from "./TabContentEditor";
import DocumentsMenu from "./DocumentsMenu";
import TabWorkflowBar from "./TabWorkflowBar";
import LocationAnalysisCard from "./LocationAnalysisCard";
import DictationButton from "./DictationButton";
import AiInsight from "./AiInsight";
import DossierDocumentsBoard from "./DossierDocumentsBoard";
import TabFeed from "./TabFeed";
import DemarchesActions from "./demarches/DemarchesActions";
import SavDossierTab from "./sav/SavDossierTab";
import { TabStatusHistory, TabStatusPicker, useTabStatusConfig } from "./TabStatusControl";

export const TAB_DEFS = [
  { key: "synthese", label: "Synthèse du dossier", icon: LayoutDashboard, impl: "synthese" },
  { key: "verif_documents", label: "Vérif documents", icon: FileCheck2, phase: 2, impl: "verif" },
  { key: "finance", label: "Finance client", icon: Banknote, phase: 2, impl: "editor" },
  { key: "demarches", label: "Démarches", icon: FileSignature, phase: 2, impl: "editor" },
  { key: "installation", label: "Installation", icon: Hammer, phase: 3, impl: "editor" },
  { key: "comptabilite", label: "Comptabilité", icon: Calculator, phase: 2, impl: "editor" },
  { key: "sav", label: "SAV", icon: LifeBuoy, phase: 6, impl: "editor" },
  { key: "documents", label: "Dossier", icon: FolderOpen, phase: 2, impl: "board" },
];

function PhasePlaceholder({ tab }) {
  const Icon = tab.icon;
  return (
    <div className="card-flat p-10 text-center" data-testid={`placeholder-${tab.key}`}>
      <div className="w-14 h-14 rounded-full bg-norea-surfaceHover text-norea-primary mx-auto flex items-center justify-center mb-4">
        <Icon className="w-7 h-7" />
      </div>
      <h3 className="font-heading text-lg font-medium text-norea-text">{tab.label}</h3>
      <div className="text-xs text-norea-textMuted mt-1 inline-flex items-center gap-1.5">
        <Clock className="w-3 h-3" /> Arrive en Phase {tab.phase}
      </div>
      <div className="mt-4 text-sm text-norea-textMuted max-w-md mx-auto">
        {tab.key === "finance" && "Multi-champs configurables + PJ par champ + workflow éditable (reste à vivre, contrôle qualité, envoi financement)."}
        {tab.key === "demarches" && "Mairie, Consuel, Raccordement, récupération de TVA — champs et étapes éditables depuis Paramètres."}
        {tab.key === "installation" && "Logique conditionnelle selon le produit (PV, ITE, PAC Air-Air, PAC Air-Eau) — workflow spécifique par type."}
        {tab.key === "comptabilite" && "Réception, analyse et paiement des factures. Labellisation « dossier payé » en fin de cycle."}
        {tab.key === "documents" && "Vue consolidée de tous les documents du dossier avec statut par type."}
      </div>
    </div>
  );
}

function SyntheseCustomFields({ dossier, produit, onUpdated }) {
  const [has, setHas] = useState(false);
  React.useEffect(() => { api.get("/configuration/tab-schema/synthese").then((r) => setHas((r.data.fields || []).some((f) => !f.hidden))).catch(() => {}); }, []);
  if (!has) return null;
  return <div className="mt-6" data-testid="synthese-custom-fields"><TabContentEditor dossierId={dossier.id} tab="synthese" produit={produit} phones={[]} onLinkedChange={() => onUpdated?.()} /></div>;
}

const SHORTCUTS = {
  installation: { label: "installation", pose: true, filter: (i) => i.group === "Installation" },
  finance: { label: "financement", pose: false, filter: (i) => i.group === "Financement" || i.id === "renonciation" },
};

export default function DossierPhaseTabs({ dossier, onDossierUpdated, syntheseContent, verifDocumentsContent }) {
  const [active, setActive] = useState("synthese");
  React.useEffect(() => {
    const go = () => setActive("documents");
    const open = (e) => { if (e.detail) { setActive(e.detail === "desinstallation" ? "sav" : e.detail); window.scrollTo({ top: 0, behavior: "smooth" }); } };
    window.addEventListener(OPEN_SLOT_EVENT, go);
    window.addEventListener("norea:open-tab", open);
    return () => { window.removeEventListener(OPEN_SLOT_EVENT, go); window.removeEventListener("norea:open-tab", open); };
  }, []);
  const [editorKey, setEditorKey] = useState(0);
  const [statuses, setStatuses] = useState(() => ({ ...(dossier.tab_statuses || {}) }));
  const [verifWorkflow, setVerifWorkflow] = useState(null);
  const [verifSchema, setVerifSchema] = useState(null);

  React.useEffect(() => {
    setStatuses({ ...(dossier.tab_statuses || {}) });
  }, [dossier.id, dossier.tab_statuses]);

  // Load the Vérif docs workflow schema + statuses so we can render the mini-bar on top of the existing content
  React.useEffect(() => {
    Promise.all([
      api.get(`/configuration/tab-schema/verif_documents`).then((r) => r.data).catch(() => null),
      api.get(`/dossiers/${dossier.id}/tab-data/verif_documents`).then((r) => r.data).catch(() => null),
    ]).then(([s, d]) => {
      setVerifSchema(s);
      setVerifWorkflow(d?.workflow_statuses || {});
    });
  }, [dossier.id]);

  const reloadVerif = () => {
    api.get(`/dossiers/${dossier.id}/tab-data/verif_documents`).then((r) => setVerifWorkflow(r.data?.workflow_statuses || {})).catch(() => {});
  };

  const statusConfig = useTabStatusConfig();
  const [statusRefresh, setStatusRefresh] = useState(0);
  const changeTabStatus = async (tabKey, next) => {
    const current = statuses[tabKey];
    setStatuses((s) => ({ ...s, [tabKey]: next }));
    try {
      await api.post(`/dossiers/${dossier.id}/tab-status`, { tab: tabKey, status: next });
      setStatusRefresh((k) => k + 1);
      onDossierUpdated?.();
    } catch {
      setStatuses((s) => ({ ...s, [tabKey]: current }));
    }
  };

  return (
    <div className="mt-6" data-testid="dossier-phase-tabs">
      {/* Tabs bar */}
      <div className="flex items-stretch border-b border-norea-border" role="tablist" data-testid="dossier-tabs-bar">
        {TAB_DEFS.map((tab) => {
          const Icon = tab.icon;
          const isActive = active === tab.key;
          const status = statuses[tab.key] || "pending";
          return (
            <div
              key={tab.key}
              className={`flex-1 min-w-0 flex flex-col gap-1 px-3 pt-2 pb-2 border-b-2 transition-colors ${
                isActive ? "border-norea-primary bg-norea-primary/5" : "border-transparent hover:bg-norea-surfaceHover"
              }`}
            >
              <div className="h-4 flex items-center min-w-0 max-w-full overflow-hidden">
                <TabStatusPicker statuses={statusConfig[tab.key]} value={status} onChange={(v) => changeTabStatus(tab.key, v)} testid={`tab-status-${tab.key}`} />
              </div>
              <button
                role="tab"
                aria-selected={isActive}
                onClick={() => setActive(tab.key)}
                className={`flex items-center gap-1.5 text-left text-[13px] font-medium leading-tight transition-colors ${
                  isActive ? "text-norea-primary" : "text-norea-textMuted hover:text-norea-text"
                }`}
                data-testid={`tab-${tab.key}`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate" title={tab.label}>{tab.label}</span>
              </button>
            </div>
          );
        })}
      </div>

      {/* Tab panels */}
      <div className="pt-5" role="tabpanel">
        <TabStatusHistory dossierId={dossier.id} tab={active} statuses={statusConfig[active]} value={statuses[active] || "pending"} onChange={(v) => changeTabStatus(active, v)} refreshKey={statusRefresh} />
        {/* Synthèse IA : uniquement dans l'onglet Synthèse du dossier (retour Norea) */}
        {active === "synthese" ? (
          <>{syntheseContent}<SyntheseCustomFields dossier={dossier} produit={dossier.produit} onUpdated={onDossierUpdated} /></>
        ) : active === "documents" ? (
          <div className="space-y-4">
            <div className="flex justify-end" data-testid="tab-docs-shortcut-documents"><DocumentsMenu dossier={dossier} testid="tab-documents-menu" onSent={() => setEditorKey((k) => k + 1)} /></div>
            <DossierDocumentsBoard key={editorKey} dossierId={dossier.id} onChanged={() => onDossierUpdated?.()} />
          </div>
        ) : active === "sav" ? (
          <SavDossierTab dossier={dossier} />
        ) : active === "verif_documents" ? (
          <>
            <TabWorkflowBar
              steps={verifSchema?.workflow || []}
              statuses={verifWorkflow || {}}
              dossierId={dossier.id}
              tab="verif_documents"
              onChange={reloadVerif}
            />
            {verifDocumentsContent}
          </>
        ) : (() => {
          const tab = TAB_DEFS.find((t) => t.key === active);
          if (tab?.impl === "editor") {
            // For "installation", pass the dossier's produit so the schema loader can
            // merge the right variant (PV / ITE / PAC Air-Air / PAC Air-Eau).
            const produit = dossier.produit;
            const editor = (
              <div className="space-y-4">
                {SHORTCUTS[active] && (
                  <div className="flex justify-end" data-testid={`tab-docs-shortcut-${active}`}>
                    <DocumentsMenu dossier={dossier} filter={SHORTCUTS[active].filter} showPose={SHORTCUTS[active].pose} label={`Documents ${SHORTCUTS[active].label}`} testid={`tab-${active}-menu`} />
                  </div>
                )}
                {active !== "documents" && (
                  <div className="flex items-center justify-between gap-4 rounded-lg border border-norea-primary/20 bg-norea-primary/5 px-4 py-3" data-testid="dictation-bar">
                    <div className="text-sm text-norea-text">
                      <span className="font-medium">Dictée intelligente</span>
                      <span className="text-norea-textMuted"> — parlez naturellement, l'IA remplit les champs et coche les étapes de cet onglet.</span>
                    </div>
                    <DictationButton dossierId={dossier.id} target={active} onApplied={() => setEditorKey((k) => k + 1)} testid={`dictation-btn-${active}`} />
                  </div>
                )}
                <TabContentEditor key={`${active}-${editorKey}`} dossierId={dossier.id} tab={active} produit={produit} phones={[dossier.extraction?.telephone, dossier.extraction?.telephone_2]} onLinkedChange={() => onDossierUpdated?.()} />
              </div>
            );
            if (active === "demarches") {
              return (
                <div className="space-y-5">
                  <LocationAnalysisCard dossier={dossier} onUpdate={() => onDossierUpdated?.()} />
                  <DemarchesActions dossier={dossier} onSent={() => { setEditorKey((k) => k + 1); onDossierUpdated?.(); }} />
                  {editor}
                </div>
              );
            }
            return editor;
          }
          return <PhasePlaceholder tab={tab} />;
        })()}
        {active !== "documents" && <TabFeed key={active} dossierId={dossier.id} tab={active} refreshKey={`${statusRefresh}-${editorKey}-${dossier.updated_at}`} />}
      </div>
    </div>
  );
}
